'use client';

import { Button } from '@govinity/ui/components/ui/button';
import { StatusBadge } from '@govinity/ui/components/ui/status-badge';
import { useToast } from '@govinity/ui/hooks/use-toast';
import { downloadDocument } from '@govinity/ui/lib/utils';
import { formatMeetingDate } from '@govinity/shared-config';
import { MeetingPublicCommentStatus } from '@govinity/validation';
import type {
  PublicCommentAttachment,
  PublicCommentUserComment,
} from '@govinity/validation/public';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

import api from '@/lib/api';
import { useConfirmationStore } from '@/stores/confirmationStore';
import { publicCommentUserCommentsQueryKey } from './helpers';
import { useGovernmentContext } from '../../../context';
import { useMeetingContext } from '../meeting-context';

type CommentListProps = {
  records: PublicCommentUserComment[];
};

export const CommentList = ({ records }: CommentListProps) => {
  const { government } = useGovernmentContext();
  const { meeting } = useMeetingContext();
  const t = useTranslations('PortalPublicComments');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const showConfirmation = useConfirmationStore((state) => state.showConfirmation);

  const {
    mutate: deleteComment,
    isPending: isDeleting,
    variables: deletingCommentId,
  } = useMutation({
    mutationFn: (commentId: number) =>
      api.governments.publicComments(government.code, meeting.id).deleteUserComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: publicCommentUserCommentsQueryKey(government.code, meeting.id),
      });
      toast({ title: t('deletedToast') });
    },
    onError: (mutationError) => {
      toast({
        title: t('deleteErrorTitle'),
        description: mutationError instanceof Error ? mutationError.message : undefined,
        variant: 'destructive',
      });
    },
  });

  const handleDownload = async ({ name, signedUrl }: PublicCommentAttachment) => {
    try {
      await downloadDocument(signedUrl, name);
    } catch {
      toast({ title: t('attachmentDownloadErrorTitle'), variant: 'destructive' });
    }
  };

  const handleDelete = (commentId: number, entryName: string) => {
    showConfirmation({
      title: t('deleteConfirmTitle'),
      description: t('deleteConfirmDescription', { name: entryName }),
      confirmText: t('deleteConfirmText'),
      confirmVariant: 'destructive',
      onConfirm: () => deleteComment(commentId),
    });
  };

  return (
    <ul className="space-y-3">
      {records.map(({ attachment, comment, createdAt, id, section, status }) => (
        <li key={id} className="space-y-1 border-b py-1 last:border-b-0">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">
              {t('commentedOnTitle', {
                name: section.name,
                date: formatMeetingDate(createdAt, {
                  timezone: government.timezone,
                  format: 'EEEE, MMMM do yyyy',
                }),
              })}
            </span>
            <div className="flex shrink-0 items-center gap-2">
              {status === MeetingPublicCommentStatus.pending ? (
                <StatusBadge variant="warning">{t('statusPending')}</StatusBadge>
              ) : (
                <StatusBadge variant="success">{t('statusApproved')}</StatusBadge>
              )}
              {status === MeetingPublicCommentStatus.pending && (
                <Button
                  aria-label={t('deleteAriaLabel', { name: section.name })}
                  disabled={isDeleting}
                  onClick={() => handleDelete(id, section.name)}
                  size="xs"
                  variant="destructive-outline"
                >
                  <Trash2 aria-hidden="true" />
                  {isDeleting && deletingCommentId === id ? t('deletingButton') : t('deleteButton')}
                </Button>
              )}
            </div>
          </div>
          <p className="text-muted-foreground text-sm whitespace-pre-wrap">{comment}</p>
          {attachment && (
            <Button
              aria-label={t('attachmentLinkAriaLabel', { name: attachment.name })}
              onClick={() => handleDownload(attachment)}
              size="xs"
              variant="outline"
            >
              <Download aria-hidden="true" />
              {attachment.name}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
};
